import { addDays, format, getDay } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { de } from 'date-fns/locale/de';
import { enUS } from 'date-fns/locale/en-US';
import i18n from 'i18n';
import _ from 'lodash';
import Logger from 'logger';

export const formatDateTime = (date: string | number, dateTimeFormat: string) => {
  const lang = i18n.languages[0];
  const locale = lang.includes('en') ? enUS : de;

  try {
    return format(new Date(date), dateTimeFormat, { locale });
  } catch (e) {
    Logger.error(e);
    return format(new Date(date), 'Pp', { locale });
  }
};

export const formatDateTimeInTimeZone = (date: string | number, dateTimeFormat: string, timeZone: string) => {
  const lang = i18n.languages[0];
  const locale = lang.includes('en') ? enUS : de;

  try {
    return formatInTimeZone(new Date(date), timeZone, dateTimeFormat, { locale });
  } catch (e) {
    Logger.error(e);
    return format(new Date(date), 'Pp', { locale });
  }
};

const dayMapping: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};

export function currentlyInWorkTime(currentDate: Date, workDays: string[], workHours: number[]): boolean {
  const sortedWorkHours = workHours.sort((a, b) => a - b);
  const sortedWorkDays = workDays.map((day) => dayMapping[day]).sort((a, b) => a - b);

  const currentWeekDay = getDay(currentDate);
  const currentHour = currentDate.getHours();

  if (sortedWorkDays.includes(currentWeekDay)) {
    if (sortedWorkHours.includes(currentHour)) {
      return true;
    }
  }

  return false;
}

export function getNextFormatWorkTime(currentDate: Date, workDays: string[], workHours: number[], timeZone: string): string | undefined {
  if (_.isEmpty(workDays)) return;
  if (_.isEmpty(workHours)) return;

  try {
    const sortedWorkHours = workHours.sort((a, b) => a - b);
    const sortedWorkDays = workDays.map((day) => dayMapping[day]).sort((a, b) => a - b);

    const currentWeekDay = getDay(currentDate);
    const currentHour = currentDate.getHours();

    // Check if today is a workday
    if (sortedWorkDays.includes(currentWeekDay)) {
      // Check if currently in work time
      if (sortedWorkHours.includes(currentHour)) {
        return i18n.t('campaignWorking');
      }

      // Find the next work hour today
      for (const hour of sortedWorkHours) {
        if (hour > currentHour) {
          const nextWorkDate = new Date(currentDate);
          nextWorkDate.setHours(hour, 0, 0, 0);
          return formatDateTimeInTimeZone(nextWorkDate.toISOString(), 'PPPPp', timeZone);
        }
      }
    }

    // If today is not a workday or no valid hours left, find the next available workday
    for (let i = 1; i <= 7; i++) {
      const potentialDate = addDays(currentDate, i);
      const potentialWeekDay = getDay(potentialDate);

      if (sortedWorkDays.includes(potentialWeekDay)) {
        const nextWorkDate = new Date(potentialDate);
        nextWorkDate.setHours(sortedWorkHours[0], 0, 0, 0);
        return formatDateTimeInTimeZone(nextWorkDate.toISOString(), 'PPPPp', timeZone);
      }
    }

    return undefined;
  } catch (e) {
    console.log(e);
    return undefined;
  }
}

export function getNextISOWorkTime(currentDate: Date, workDays: string[], workHours: number[]): string | undefined {
  if (_.isEmpty(workDays)) return;
  if (_.isEmpty(workHours)) return;

  try {
    const sortedWorkHours = workHours.sort((a, b) => a - b);
    const sortedWorkDays = workDays.map((day) => dayMapping[day]).sort((a, b) => a - b);

    const currentWeekDay = getDay(currentDate);
    const currentHour = currentDate.getHours();

    // Check if today is a workday
    if (sortedWorkDays.includes(currentWeekDay)) {
      // Check if currently in work time
      if (sortedWorkHours.includes(currentHour)) {
        return undefined;
      }

      // Find the next work hour today
      for (const hour of sortedWorkHours) {
        if (hour > currentHour) {
          const nextWorkDate = new Date(currentDate);
          nextWorkDate.setHours(hour, 0, 0, 0);
          return nextWorkDate.toISOString();
        }
      }
    }

    // If today is not a workday or no valid hours left, find the next available workday
    for (let i = 1; i <= 7; i++) {
      const potentialDate = addDays(currentDate, i);
      const potentialWeekDay = getDay(potentialDate);

      if (sortedWorkDays.includes(potentialWeekDay)) {
        const nextWorkDate = new Date(potentialDate);
        nextWorkDate.setHours(sortedWorkHours[0], 0, 0, 0);
        return nextWorkDate.toISOString();
      }
    }

    return undefined;
  } catch (e) {
    console.log(e);
    return undefined;
  }
}
